import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/Dialog/Dialog';

import { MediaLibraryBody } from '../MediaLibraryBody/MediaLibraryBody';

import { type MediaLibraryDialogProps } from './MediaLibraryDialog.types';

/**
 * "Pick from the library" — the half that makes this a picker rather than an
 * uploader.
 *
 * The shell only. Everything that reads or pages lives in `MediaLibraryBody`,
 * which Radix mounts when the dialog opens and unmounts when it closes — so the
 * widened page resets by unmounting rather than by an effect, and reopening
 * never lands on a page from a previous session.
 */
export function MediaLibraryDialog({
  isOpen,
  onOpenChange,
  kind,
  value,
  onChoose,
  source,
  onUploadRequested,
}: MediaLibraryDialogProps) {
  const { t } = useTranslation('uiKit');

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{t('media.library.title')}</DialogTitle>
          <DialogDescription>{t('media.library.description')}</DialogDescription>
        </DialogHeader>

        <MediaLibraryBody
          kind={kind}
          value={value}
          source={source}
          onChoose={onChoose}
          onUploadRequested={onUploadRequested}
        />

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {t('media.library.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
